import { NextPage } from 'next';
import { useFormik } from 'formik';
import clsx from 'clsx';
import Image from 'next/image';
import Head from 'next/head';
import { Button } from '../components/Button';
import { PageLayout } from '../components/PageLayout';
import * as Yup from 'yup';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useState } from 'react';

enum TicketCategory {
  PRODUCT_ISSUE = 'PRODUCT_ISSUE',
  CAREERS = 'CAREERS',
  FEEDBACK = 'FEEDBACK',
  GENERAL_INQUIRY = 'GENERAL_INQUIRY',
  OTHER = 'OTHER',
}

const TICKET_CATEGORIES: Array<{ value: TicketCategory; label: string }> = [
  {
    value: TicketCategory.PRODUCT_ISSUE,
    label: 'Problem with the app',
  },
  {
    value: TicketCategory.CAREERS,
    label: 'Careers with kliggit',
  },
  {
    value: TicketCategory.FEEDBACK,
    label: 'General feedback',
  },
  {
    value: TicketCategory.GENERAL_INQUIRY,
    label: 'Just wanna say hey!',
  },
  {
    value: TicketCategory.OTHER,
    label: 'Other',
  },
];

const validationSchema = Yup.object().shape({
  category: Yup.string(),
  content: Yup.string().required(
    'Please type your reason for contacting us in the message box.',
  ),
  email: Yup.string()
    .email('This is an invalid email. Please try again.')
    .required('Email is required'),
});

const RECAPTCHA_ACTION = 'contact';

const Contact: NextPage = () => {
  const [success, setSuccess] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const {
    errors,
    handleChange,
    handleSubmit,
    setFieldValue,
    setFieldError,
    values,
  } = useFormik({
    initialValues: {
      category: TicketCategory.PRODUCT_ISSUE,
      content: '',
      email: '',
    },
    onSubmit: async values => {
      if (!executeRecaptcha) return;
      const token = await executeRecaptcha(RECAPTCHA_ACTION);

      try {
        const response = await fetch('/api/hubspot/contact', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'g-recaptcha-response': token,
            'g-recaptcha-action': RECAPTCHA_ACTION,
          },
          body: JSON.stringify({
            'TICKET.hs_ticket_category': values.category,
            'TICKET.subject': `Website Contact - ${values.category}`,
            'TICKET.content': values.content,
            email: values.email,
          }),
        });

        await response.json();

        if (!response.ok) {
          setFieldError('content', 'An error occurred');
        }
      } catch (err) {
        setFieldError('content', 'An error occurred');
      }

      setSuccess(true);
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
  });

  return (
    <PageLayout>
      <Head>
        <title>kliggit | Contact</title>
        <meta
          name="description"
          content="If you have any questions, concerns, or issues we are here to help. Check the FAQs or contact us if you’d rather do that."
        />
      </Head>
      <h1 className="mt-48 mb-3 font-semibold lg:mt-0">How can we help?</h1>
      <main className="mb-24">
        <form className="max-w-lg" onSubmit={handleSubmit}>
          <div className="flex flex-wrap gap-2 mb-6">
            {TICKET_CATEGORIES.map(category => {
              const className = clsx(
                'px-3 py-2 hover:text-brand-white hover:border-brand-white hover:cursor-pointer rounded w-max border',
                {
                  'text-brand-gray-3 border-brand-gray-1':
                    values.category !== category.value,
                  'text-brand-white border-brand-white':
                    values.category === category.value,
                },
              );
              return (
                <div
                  key={category.value}
                  className={className}
                  onClick={() => setFieldValue('category', category.value)}>
                  <input
                    checked={category.value === values.category}
                    hidden
                    id={`category_${category.value}`}
                    name="category"
                    onChange={handleChange}
                    type="radio"
                    value={category.value}
                  />
                  <label
                    className="hover:cursor-pointer"
                    htmlFor={`category_${category.value}`}>
                    {category.label}
                  </label>
                </div>
              );
            })}
          </div>
          <textarea
            className="p-3 mb-6 w-full rounded resize-none bg-brand-gray-1 text-brand-gray-3 focus:text-brand-white"
            name="content"
            onChange={handleChange}
            placeholder="Tell us about it..."
            rows={5}
            value={values.content}
          />
          <div className="flex">
            <input
              className="flex-1 py-2 px-3 mr-3 rounded bg-brand-gray-1 text-brand-gray-3 focus:text-brand-white"
              name="email"
              onChange={handleChange}
              placeholder="Enter email"
              type="email"
            />
            <Button type="submit">Submit</Button>
          </div>
          <div className="mt-2 text-sm text-risk">
            {errors &&
              Object.entries(errors).map(([key, value]) => {
                return <p key={key}>{value}</p>;
              })}
          </div>
          {success && (
            <div className="mt-2 text-sm text-success">
              <p>Thank you for your submission.</p>
            </div>
          )}
        </form>
      </main>
      <div className="absolute -right-24 top-28 w-full transform rotate-6 sm:top-36 sm:w-3/4 md:-right-36 lg:w-3/5 xl:-right-56 -z-1">
        <Image
          alt=""
          height={673}
          layout="responsive"
          objectFit="contain"
          src="/images/contact/contact_phone.png"
          width={794}
        />
      </div>
    </PageLayout>
  );
};

export default Contact;
